import React, { useEffect, useState } from 'react';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import PropTypes from 'prop-types';
import { getResponsesByDay } from 'services/surveys';
import FalconCardHeader from 'components/common/FalconCardHeader';

// Registrar los componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SurveyResponsesByDay = ({ surveyId, className = '' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (surveyId) {
      fetchData();
    }
  }, [surveyId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getResponsesByDay(surveyId);
      console.log(response);
      if (response && Array.isArray(response)) {
        setData(response);
      } else {
        setError('No se pudieron cargar los datos de respuestas por día');
        setData([]);
      }
    } catch (error) {
      console.error('Error al cargar respuestas por día:', error);
      setError('Error al cargar los datos');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Configuración del gráfico
  const chartData = {
    labels: data.map(item => {
      const date = new Date(item.date);
      // Para móvil, usar formato más corto
      if (isMobile) {
        if (data.length > 15) {
          // Formato ultra corto para muchos datos
          return date.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'numeric'
          });
        } else {
          return date
            .toLocaleDateString('es-ES', {
              day: '2-digit',
              month: 'short'
            })
            .replace('.', '');
        }
      }
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit'
      });
    }),
    datasets: [
      {
        label: 'Respuestas por día',
        data: data.map(item => item.responses),
        backgroundColor: 'rgba(54, 162, 235, 1)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y} respuestas`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          font: {
            size: isMobile ? 9 : 11
          }
        },
        title: {
          display: !isMobile,
          text: 'Número de Respuestas',
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      x: {
        ticks: {
          maxRotation: isMobile ? 90 : 45,
          minRotation: isMobile ? 45 : 0,
          font: {
            size: isMobile ? 9 : 11
          },
          // Mostrar todas las etiquetas de forma responsiva
          maxTicksLimit: isMobile ? Math.min(data.length, 8) : undefined
        },
        title: {
          display: !isMobile,
          text: 'Fecha',
          font: {
            size: isMobile ? 10 : 12
          }
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    // Mejorar el rendimiento para muchos datos
    animation: {
      duration: isMobile ? 500 : 1000
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Cargando gráfico de respuestas por día...</span>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <Card.Body>
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className={className}>
        <Card.Body className="text-center py-4">
          <Alert variant="info" className="mb-0">
            No hay datos de respuestas por día disponibles.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <FalconCardHeader
        title="Respuestas por Día"
        light={false}
        titleTag="h5"
        className="pb-0"
      />
      <Card.Body className="pt-2">
        <div
          style={{
            height: isMobile ? '300px' : '400px'
          }}
        >
          <Bar data={chartData} options={chartOptions} />
        </div>
        <div className="mt-3">
          <small className="text-muted">
            Total de días con respuestas: {data.length}
          </small>

          {data.length > 0 && (
            <div className="mt-3   rounded">
              <h6 className="mb-2 text-primary" style={{ fontSize: '0.85rem' }}>
                📊 Top 5 Días con Más Respuestas
              </h6>
              <div className="row g-2">
                {data
                  .sort((a, b) => b.responses - a.responses)
                  .slice(0, 5)
                  .map((item, index) => {
                    const date = new Date(item.date);
                    const formattedDate = date.toLocaleDateString('es-ES', {
                      weekday: 'short',
                      day: '2-digit',
                      month: 'short'
                    });

                    return (
                      <div key={index} className="col-12 col-sm-6 col-lg-4">
                        <div className="d-flex justify-content-between align-items-center py-1 px-2 bg-white rounded border">
                          <div className="d-flex align-items-center">
                            <span
                              className="badge bg-primary me-2"
                              style={{ fontSize: '0.7rem', minWidth: '20px' }}
                            >
                              #{index + 1}
                            </span>
                            <small
                              className="text-dark fw-medium"
                              style={{ fontSize: '0.75rem' }}
                            >
                              {formattedDate}
                            </small>
                          </div>
                          <span
                            className="badge bg-success"
                            style={{ fontSize: '0.7rem' }}
                          >
                            {item.responses} respuestas
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
              {data.length > 5 && (
                <div className="mt-2 text-center">
                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Mostrando los 5 días con mejores resultados de {data.length}{' '}
                    días totales
                  </small>
                </div>
              )}
            </div>
          )}

          {isMobile && data.length > 8 && (
            <div className="mt-1">
              <small className="text-info">
                💡 Algunas fechas se muestran de forma simplificada para mejor
                visualización
              </small>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

SurveyResponsesByDay.propTypes = {
  surveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  className: PropTypes.string
};

export default SurveyResponsesByDay;
